import React from 'react'
import styled from 'styled-components'
import { Card, CardBody, Flex, Heading, useMatchBreakpoints, Text } from 'penguinfinance-uikit2'
import useI18n from 'hooks/useI18n'
import BigNumber from 'bignumber.js'
import { getBalanceNumber } from 'utils/formatBalance'
import { useTotalSupply, useBurnedBalance } from 'hooks/useTokenBalance'
import { getPefiAddress } from 'utils/addressHelpers'
import {
  useFarms,
  useV2Farms,
  usePriceAvaxUsdt,
  usePricePefiUsdt,
  usePriceEthUsdt,
  usePricePngUsdt,
  usePriceLinkUsdt,
  usePriceLydUsdt,
  useCompounderFarms
} from 'state/hooks'
import { QuoteToken } from 'config/constants/types'
import { PEFI_MAX_SUPPLY } from 'config'
import SvgIcon from 'components/SvgIcon'
import CardValue from './CardValue';

const StyledContainer = styled.div<{ isMobile?: boolean }>`
  position: relative;

  @media (min-width: 968px) {
    margin-top: 200px;
  }
  @media (min-width: 1200px) {
    margin-top: 300px;
  }
  @media (min-width: 1400px) {
    margin-top: 360px;
  }
`

const StyledSubContainer = styled.div`
  position: relative;
`

const StyledTotalValueLockedCard = styled(Card)<{ isMobile?: boolean }>`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  margin-top: ${({ isMobile }) => (isMobile ? 10 : -108)}px;
  overflow: visible;
  background-color: ${({ theme }) => theme.isDark ? '#272044' : '#392771'};
  border-radius: 16px;

  @media (min-width: 1200px) {
    border-radius: 60px;
  }
`

const FarmerImg = styled.img`
  position: absolute;
  width: 660px;
  top: -410px;
  left: -20px;
  display: none;

  @media (min-width: 1200px) {
    display: block;
    width: 520px;
    top: -320px;
    left: -20px;
  }
  @media (min-width: 1400px) {
    width: 660px;
    top: -410px;
    left: -20px;
  }
`;

const HomeMagoImg = styled.img`
  position: absolute;
  right: 0;
  width: 360px;
  right: 0px;
  top: 160px;

  @media (min-width: 640px) {
    width: 400px;
    right: 80px;
    top: 180px;
  }
  @media (min-width: 968px) {
    width: 520px;
    right: 0px;
    top: -310px;
  }
  @media (min-width: 1200px) {
    width: 520px;
    right: 80px;
    top: -310px;
  }
  @media (min-width: 1400px) {
    width: 640px;
    right: 40px;
    top: -372px;
  }
`;

const MagoImg = styled.img`
  position: absolute;
  right: 0;
  width: 240px;
  right: -60px;
  top: 180px;

  @media (min-width: 640px) {
    width: 300px;
    right: -60px;
    top: 180px;
  }
  @media (min-width: 968px) {
    width: 360px;
    right: -100px;
    top: -300px;
  }
  @media (min-width: 1200px) {
    width: 440px;
    right: -120px;
    top: -360px;
  }
  @media (min-width: 1400px) {
    width: 510px;
    right: -140px;
    top: -420px;
  }
`;

const StyledHeading = styled(Heading)`
  font-size: 24px;
  font-weight: 300;
  color: #EBF6FF;
`

const StyledMainHeading = styled(StyledHeading)`
  font-size: 12px;
  line-height: 18px;
  font-weight: 300;
  text-transform: uppercase;
  color: white;

  @media (min-width: 968px) {
    font-size: 24px;
    line-height: 36px;
  }
  @media (min-width: 1200px) {
    font-size: 28px;
    line-height: 42px;
  }
`

const StyledValueHeading = styled(CardValue)`
  font-size: 40px;
  line-height: 56px;
  font-weight: bold;
  color: white;
  margin-bottom: 24px;

  @media (min-width: 640px) {
    font-size: 48px;
    margin-bottom: 40px;
  }
  @media (min-width: 968px) {
    font-size: 64px;
    margin-bottom: 0;
  }
  @media (min-width: 1200px) {
    font-size: 76px;
  }
`

const StyledCardBody = styled(CardBody)`
  flex: 1;
  width: 100%;
  padding: 32px 16px 140px;

  @media (min-width: 640px) {
    padding: 32px 40px 160px;
  }
  @media (min-width: 968px) {
    padding: 32px 40px 44px;
  }
  @media (min-width: 1200px) {
    padding: 32px 72px 44px;
  }
`

const StyledSubCardBody = styled(CardBody)`
  padding: 34px 10px 34px 20px;
  border-radius: 20px;
`

const StyledCardValue = styled(CardValue)`
  color: #EBF6FF;
  font-weight: bold;
  font-size: 14px;

  @media (min-width: 1200px) {
    font-size: 20px;
    line-height: 24px;
  }
`

const StyledText = styled(Text)`
  margin-top: 4px;
  margin-bottom: 16px;
  color: #EBF6FF;
  font-family: Kanit;
  font-style: normal;
  font-weight: 300;
  font-size: 11px;
  line-height: 17px;

  @media (min-width: 640px) {
    font-size: 17px;
    line-height: 24px;
  }

  @media (min-width: 968px) {
    margin-top: 12px;
    margin-bottom: 0;
  }
`

const InfoIconWrapper = styled.div`
  svg {
    cursor: pointer;
    margin-left: 16px;
    margin-top: 4px;
    path {
      fill: white;
    }
  }
`

const StyledCard = styled(Card)`
  margin-top: 20px;
  background: ${({ theme }) => theme.isDark ? '#483692' : '#564BAB'};
  border-radius: 20px;
`;

const TotalValueLockedCard = ({ v1Pool, v2Pool }) => {
  const TranslateString = useI18n()
  const { isXl } = useMatchBreakpoints()
  const isMobile = !isXl

  const totalSupply = useTotalSupply()
  const burnedBalance = useBurnedBalance(getPefiAddress())
  const farmsLP = useFarms()
  const v2Farms = useV2Farms()
  const compounderFarms = useCompounderFarms()
  const pefiPrice = usePricePefiUsdt()
  const avaxPrice = usePriceAvaxUsdt()
  const ethPriceUsd = usePriceEthUsdt()
  const pngPriceUsd = usePricePngUsdt()
  const linkPriceUsd = usePriceLinkUsdt()
  const lydPriceUsd = usePriceLydUsdt()
  // const { isDark } = useTheme()
  const iPefiToPefiRatio = v2Pool.currentExchangeRate || 1

  const getXPefiToPefiRatio = () => {
    return v1Pool.totalStaked && v1Pool.totalSupply
      ? new BigNumber(v1Pool.totalStaked).div(new BigNumber(v1Pool.totalSupply)).toNumber()
      : 1
  }

  const getTokenPrice = (tokenSymbol: string) => {
    if (tokenSymbol === QuoteToken.PEFI) return pefiPrice
    if (tokenSymbol === QuoteToken.AVAX) return avaxPrice
    if (tokenSymbol === QuoteToken.ETH) return ethPriceUsd
    return new BigNumber(1)
  }

  // calculate TVL in old farms
  const getFarmsTVL = () => {
    let igloosTVL = new BigNumber(0)
    farmsLP.map((farmLP) => {
      const farmQuoteTokenPrice = getTokenPrice(farmLP.quoteTokenSymbol)
      if (farmLP.quoteTokenAmount) {
        const iglooTVL = farmQuoteTokenPrice.times(new BigNumber(farmLP.quoteTokenAmount)).times(new BigNumber(2))
        if (iglooTVL) igloosTVL = igloosTVL.plus(iglooTVL)
      }
      return igloosTVL
    })
    return igloosTVL.toNumber()
  }

  // calculate TVL in v2 farms
  const getV2FarmsTVL = () => {
    let v2FarmTvl = 0
    v2Farms.map((farm) => {
      v2FarmTvl += farm.totalLiquidityInUsd || 0
      return farm
    })
    return v2FarmTvl
  }

  // calculate TVL in compounder farms
  const getCompounderFarmsTVL = () => {
    let compounderFarmsTVL = 0
    compounderFarms.map((compounderFarm) => {
      let totalValue = null
      if (!compounderFarm.lpTotalInQuoteToken) {
        totalValue = new BigNumber(0)
      } else if (compounderFarm.quoteTokenSymbol === QuoteToken.AVAX) {
        totalValue = avaxPrice.times(compounderFarm.lpTotalInQuoteToken)
      } else if (compounderFarm.quoteTokenSymbol === QuoteToken.PEFI) {
        totalValue = pefiPrice.times(compounderFarm.lpTotalInQuoteToken)
      } else if (compounderFarm.quoteTokenSymbol === QuoteToken.ETH) {
        totalValue = ethPriceUsd.times(compounderFarm.lpTotalInQuoteToken)
      } else if (compounderFarm.quoteTokenSymbol === QuoteToken.PNG) {
        totalValue = pngPriceUsd.times(compounderFarm.lpTotalInQuoteToken)
      } else if (compounderFarm.quoteTokenSymbol === QuoteToken.LYD) {
        totalValue = lydPriceUsd.times(compounderFarm.lpTotalInQuoteToken)
      } else if (compounderFarm.quoteTokenSymbol === QuoteToken.LINK) {
        totalValue = linkPriceUsd.times(compounderFarm.lpTotalInQuoteToken)
      } else {
        totalValue = compounderFarm.lpTotalInQuoteToken
      }

      if (compounderFarm.strategyRatio) {
        compounderFarmsTVL += totalValue.times(compounderFarm.strategyRatio).toNumber()
      }

      return compounderFarm
    })
    return compounderFarmsTVL
  }

  // calculate TVL in v1 pefi nest
  const getV1NestTVL = () => {
    if (v1Pool.totalSupply) return getXPefiToPefiRatio() * pefiPrice.toNumber() * getBalanceNumber(v1Pool.totalSupply)
    return 0
  }

  // calculate TVL in v2 pefi nest
  const getV2NestTVL = () => {
    if (v2Pool.totalSupply) return iPefiToPefiRatio * pefiPrice.toNumber() * getBalanceNumber(v2Pool.totalSupply)
    return 0
  }

  // get pefi marketcap
  const getPefiMarketcap = () => {
    if (totalSupply) return pefiPrice.toNumber() * (getBalanceNumber(totalSupply) - getBalanceNumber(burnedBalance))
    return 0
  }

  const tvlValue = getFarmsTVL() + getV2FarmsTVL() + getCompounderFarmsTVL() + getV1NestTVL() + getV2NestTVL()
  const pefiMarketcap = getPefiMarketcap()
  const totalStakedBalanceInV1 = new BigNumber(v1Pool.totalStaked) || new BigNumber(0)
  const totalStakedBalanceInV2 = new BigNumber(v2Pool.totalStaked) || new BigNumber(0)
  const totalStakedBalance = totalStakedBalanceInV1.plus(totalStakedBalanceInV2)
  const burnedBalanceRatio = (100 * getBalanceNumber(burnedBalance)) / getBalanceNumber(totalSupply)
  const totalStakedBalanceRatio = (100 * getBalanceNumber(totalStakedBalance)) / getBalanceNumber(totalSupply)

  return (
    <StyledContainer isMobile={isMobile}>
      {!isMobile && <FarmerImg src="/images/home-v2/farmer.svg" alt="penguin logo" />}
      <StyledTotalValueLockedCard mb={2} mt={2} isMobile={isMobile}>
        <StyledCardBody>
          <Flex alignItems='center'>
            <StyledMainHeading>{TranslateString(762, 'Total Value Locked')}</StyledMainHeading>
            <InfoIconWrapper>
              <SvgIcon
                src={`${process.env.PUBLIC_URL}/images/home-v2/info.svg`}
                width={isMobile ? '18px' : '22px'}
                height={isMobile ? '18px' : '22px'}
              />
            </InfoIconWrapper>
          </Flex>
          {/* <StyledValueHeading>{`$${tvlValue}`}</StyledValueHeading> */}
          <StyledValueHeading
            // fontSize="20px"
            prefix="$"
            decimals={0}
            bold={false}
            value={tvlValue || 0}
            updateInterval={30000}
          />
          <StyledSubContainer>
            {!isMobile && (
              <>
                <HomeMagoImg
                  src="/images/home-v2/home_mago.svg"
                  alt="penguin logo"
                />
                <MagoImg src="/images/home-v2/mago.svg" alt="penguin logo" />
              </>
            )}
            {!isMobile && (
              <StyledCard>
                <StyledSubCardBody>
                  <StyledHeading mb={2}>
                    PEFI STATS
                  </StyledHeading>
                  <Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {pefiMarketcap && (
                        <StyledCardValue fontSize="20px" prefix="$" bold={false} value={pefiMarketcap} updateInterval={30000} />
                      )}
                      <StyledText>PEFI Market Capitalization</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {totalSupply && (
                        <StyledCardValue
                          fontSize="20px"
                          suffix=" PEFI"
                          bold={false}
                          value={getBalanceNumber(totalSupply) - getBalanceNumber(burnedBalance)}
                          updateInterval={30000}
                        />
                      )}
                      <StyledText>Circulating PEFI Supply</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {burnedBalance && (
                        <StyledCardValue
                          fontSize="20px"
                          suffix={` PEFI (${burnedBalanceRatio.toFixed(0)}%)`}
                          bold={false}
                          value={getBalanceNumber(burnedBalance)}
                          decimals={0}
                          updateInterval={30000}
                        />
                      )}
                      <StyledText>Total PEFI Burned</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {totalStakedBalance && (
                        <StyledCardValue
                          fontSize="20px"
                          suffix={` PEFI (${totalStakedBalanceRatio.toFixed(0)}%)`}
                          bold={false}
                          decimals={0}
                          value={getBalanceNumber(totalStakedBalance)}
                          updateInterval={30000}
                        />
                      )}
                      <StyledText>Total PEFI staked</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      <StyledCardValue fontSize="20px" suffix=" PEFI" bold={false} value={PEFI_MAX_SUPPLY} />
                      <StyledText>Max Supply</StyledText>
                    </Flex>
                  </Flex>
                </StyledSubCardBody>
              </StyledCard>
            )}
            {isMobile && (
              <div>
                <StyledHeading mt={2} mb={2} >
                  PEFI STATS
                </StyledHeading>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  {/* <Flex> */}
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {pefiMarketcap && (
                        <StyledCardValue fontSize="20px" prefix="$" bold={false} value={pefiMarketcap} updateInterval={30000} />
                      )}
                      <StyledText>PEFI Market Capitalization</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {totalSupply && (
                        <StyledCardValue
                          fontSize="20px"
                          suffix=" PEFI"
                          bold={false}
                          value={getBalanceNumber(totalSupply) - getBalanceNumber(burnedBalance)}
                          updateInterval={30000}
                        />
                      )}
                      <StyledText>Circulating PEFI Supply</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {burnedBalance && (
                        <StyledCardValue
                          fontSize="20px"
                          suffix={` PEFI (${burnedBalanceRatio.toFixed(0)}%)`}
                          bold={false}
                          value={getBalanceNumber(burnedBalance)}
                          decimals={0}
                          updateInterval={30000}
                        />
                      )}
                      <StyledText>Total PEFI Burned</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      {totalStakedBalance && (
                        <StyledCardValue
                          fontSize="20px"
                          suffix={` PEFI (${totalStakedBalanceRatio.toFixed(0)}%)`}
                          bold={false}
                          decimals={0}
                          value={getBalanceNumber(totalStakedBalance)}
                          updateInterval={30000}
                        />
                      )}
                      <StyledText>Total PEFI staked</StyledText>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      style={{
                        borderTop: '6px solid #7E7EE2',
                        marginRight: 10,
                        paddingTop: 10,
                        marginTop: isMobile ? 15 : 0,
                      }}
                      flex={1}
                    >
                      <StyledCardValue fontSize="20px" suffix=" PEFI" bold={false} value={PEFI_MAX_SUPPLY} />
                      <StyledText>Max Supply</StyledText>
                    </Flex>
                  {/* <StatsValue value="$33,435,029" title="PEFI Market Captilazation" isMobile={isMobile} />
                  <StatsValue value="18,992,906" title="Circulating PEFI Supply" isMobile={isMobile} />
                  <StatsValue value="1,694,526 (8%)" title="Total PEFI Burned" isMobile={isMobile} />
                  <StatsValue value="33,437,029 (66%)" title="Total PEFI staked" isMobile={isMobile} />
                  <StatsValue value="21,000,000" title="Max Supply" isMobile={isMobile} /> */}
                </div>
              </div>
            )}
            {isMobile && (
              <>
                <HomeMagoImg
                  src="/images/home-v2/home_mago.svg"
                  alt="penguin logo"
                />
                <MagoImg src="/images/home-v2/mago.svg" alt="penguin logo" />
              </>
            )}
          </StyledSubContainer>
        </StyledCardBody>
      </StyledTotalValueLockedCard>
    </StyledContainer>
  )
}

export default TotalValueLockedCard
